// Verlauf der AI-Zitierungen einer URL über alle Systeme (ahrefs Brand Radar), Diagramm und Zahlen. Rein, ohne Netzwerk.
import type { OnpageAiHistory } from "./api/types";
import { formatDate } from "./format";

export type SeriesKey =
  | "total"
  | "google_ai_overviews"
  | "google_ai_mode"
  | "chatgpt"
  | "gemini"
  | "perplexity"
  | "copilot";

export type Series = { key: SeriesKey; label: string; color: string; dash: string | null };

/** „Alle Systeme zusammen“ und die sechs Plattformen. Linien unterscheiden sich in Farbe und Strichart, nie nur in der Farbe. */
export const SERIES: readonly Series[] = [
  { key: "total", label: "Alle Systeme zusammen", color: "#15191E", dash: null },
  { key: "google_ai_overviews", label: "Google AI Overview", color: "#1F5FAD", dash: null },
  { key: "google_ai_mode", label: "Google AI Mode", color: "#7B5EA7", dash: "6 3" },
  { key: "chatgpt", label: "ChatGPT", color: "#24573A", dash: null },
  { key: "gemini", label: "Gemini", color: "#C2621A", dash: "6 3" },
  { key: "perplexity", label: "Perplexity", color: "#0E7C86", dash: "2 3" },
  { key: "copilot", label: "Copilot", color: "#8E2318", dash: "2 3" },
];

type Point = OnpageAiHistory["points"][number];

/** Zahl der Zitierungen eines Systems zu einem Zeitpunkt, „Alle Systeme“ ist die Summe. Ohne Angabe null. */
export function valueAt(point: Point, key: SeriesKey): number | null {
  if (key === "total") {
    const values = Object.values(point.counts).filter((v): v is number => typeof v === "number");
    return values.length === 0 ? null : values.reduce((sum, v) => sum + v, 0);
  }
  const value = point.counts[key];
  return typeof value === "number" ? value : null;
}

export function valuesOf(history: OnpageAiHistory, key: SeriesKey): (number | null)[] {
  return history.points.map((point) => valueAt(point, key));
}

/** Erster und letzter Wert und die Veränderung, z. B. ChatGPT 18 → 60 (+42). Null, wenn ein Wert fehlt. */
export function change(history: OnpageAiHistory, key: SeriesKey): { from: number; to: number; delta: number } | null {
  const values = valuesOf(history, key);
  const from = values[0];
  const to = values[values.length - 1];
  if (from === null || from === undefined || to === null || to === undefined) return null;
  return { from, to, delta: to - from };
}

/** Obergrenze der Achse: 1, 2, 5 oder 10 mal eine Zehnerpotenz, mindestens 5. */
export function niceMax(value: number): number {
  const target = Math.max(5, value);
  const power = 10 ** Math.floor(Math.log10(target));
  for (const step of [1, 2, 5, 10]) {
    if (step * power >= target) return step * power;
  }
  return 10 * power;
}

export type Geometry = {
  width: number;
  height: number;
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export const GEOMETRY: Geometry = { width: 720, height: 280, left: 48, top: 14, right: 16, bottom: 34 };

export function xAt(index: number, count: number, g: Geometry = GEOMETRY): number {
  const inner = g.width - g.left - g.right;
  return g.left + (count <= 1 ? inner / 2 : (inner * index) / (count - 1));
}

export function yAt(value: number, max: number, g: Geometry = GEOMETRY): number {
  const inner = g.height - g.top - g.bottom;
  return g.top + inner - (inner * value) / max;
}

/** Linienzug einer Reihe. Fehlende Werte unterbrechen die Linie. */
export function pathOf(values: readonly (number | null)[], max: number, g: Geometry = GEOMETRY): string {
  let d = "";
  let open = false;
  values.forEach((value, index) => {
    if (value === null) {
      open = false;
      return;
    }
    d += `${open ? "L" : "M"}${xAt(index, values.length, g).toFixed(1)} ${yAt(value, max, g).toFixed(1)}`;
    open = true;
  });
  return d;
}

/** Datum für die Achse: „07.09.“. */
export function axisLabel(iso: string): string {
  return formatDate(iso).slice(0, 6);
}

/** Achsenbeschriftung an jedem Punkt, ab mehr als 12 Punkten nur an jedem zweiten. */
export function labelIndexes(count: number): number[] {
  const step = count > 12 ? 2 : 1;
  const out: number[] = [];
  for (let i = 0; i < count; i += step) out.push(i);
  if (count > 0 && out[out.length - 1] !== count - 1 && count - 1 - (out[out.length - 1] ?? 0) >= step) out.push(count - 1);
  return out;
}

export const SPAN_LABEL = { weeks: "12 Wochen (wöchentlich)", months: "12 Monate (alle 4 Wochen)" } as const;
